import { store, store$ } from '@redux/store';
import { googleAnalytics4Event } from 'features/googleAnalytics4';
import { filter, fromEvent, map, merge, distinctUntilChanged } from 'rxjs';
import HousingProductType from 'constants/housingProductType';
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';
import typeahead$ from '../../../../blog/articlePage/components/typeahead';

const getNavigatorItemElement = (event) => event.target.closest('[data-navigator-item]');
const breadcrumbItems = Array.from(document.querySelectorAll('[data-breadcrumb-item]'));
const searchSubmitElement = document.querySelector('[data-search-submit]');
const inputSearchBox = document.getElementById('blog_typeahead_input');
const getStandardCardElement = (event) => event.target.closest('[data-card]');
const isListingForYouSection = (event) => !!event.target.closest('[data-listings-for-you-container]');
const isFavoriteIcon = (event) => !!event.target.closest('.icon__save');
const isCallToAction = (event) => !!event.target.closest('[data-lead-generator-opener]');
const isStandardCard = (event) => !!event.target.closest('[data-standard-card]');
const isPriced = (price) => price && price !== '0';
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();

/* Observable that listens for click events on the document.body */
const bodyClick$ = fromEvent(document.body, 'click');
const listingForYouSectionClick$ =  bodyClick$.pipe(filter(event => isListingForYouSection(event)));

/**
 * getModalClosePaidCommunityCustomProperties: function to get the custom properties of close button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalClosePaidCommunityCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.priceLow,
      max_price: dataset.priceHigh,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalClosePaidPlanCustomProperties: function to get the custom properties of close button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalClosePaidPlanCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      plan_id: dataset.planId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'False',
      is_plan: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalClosePaidSpecCustomProperties: function to get the custom properties of close button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalClosePaidSpecCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',        
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      spec_id: dataset.specId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'True',
      is_plan: 'False',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getRecommendedSubmittedPaidCommunityCustomProperties: function to get custom properties of Recommended button click
* based on the community listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getRecommendedSubmittedPaidCommunityCustomProperties = function(modalInformation){
  const { selectedRecommendedCommunities } = modalInformation;
  selectedRecommendedCommunities.forEach((dataset) => {
      const customProperties = {
          page_type: 'Learning_Center',
          click_target: 'Learn Center Listings Cards',
          button_name: 'Get More Info',
          event_type: 'TDV Events',
          currency_value: 'USD',
          value: 30,
          trustbuilder_reviews: dataset.HasBuilderReviews,
          min_price: dataset.PriceLow,
          max_price: dataset.PriceHigh,
          market_id: dataset.MarketId,
          market_name: `${dataset.MarketName}, ${dataset.StateAbbreviation}`,
          community_name: dataset.CommunityName,
          community_status: dataset.MarketingStatus || 'Normal',
          community_id: dataset.CommunityId,            
          price_reduction: dataset.PriceReductionLabel,
          is_limited_availability: dataset.HasLimitedInformation,
          is_priced: isPriced(dataset.PriceLow) || isPriced(dataset.PriceHigh) ? 'True' : 'False',
          is_paid: 'True',
          zip_code: dataset.Zip,
          builder_id: dataset.BuilderId,
          builder_name: dataset.BrandName
      };

      store.dispatch(googleAnalytics4Event({ eventName: 'direct_rec_lead', customProperties }));
  })
}

/**
* getLeadButtonClickedPaidCommunityCustomProperties: function to get the custom properties of lead button click
* based on the community listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getLeadButtonClickedPaidCommunityCustomProperties = function(standarCommunityCard){
  const leadButton = standarCommunityCard.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || {};

  const dataset = standarCommunityCard.dataset;
  const destionationUrl = dataset?.url ?? standarCommunityCard?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: leadButtonDataset.buttonAction,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.priceLow,
      max_price: dataset.priceHigh,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getLeadButtonClickedPaidPlanCustomProperties: function to get the custom properties of lead button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getLeadButtonClickedPaidPlanCustomProperties = function(standarPlanCard){
  const leadButton = standarPlanCard.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || {};

  const dataset = standarPlanCard.dataset;
  const destionationUrl = dataset?.url ?? standarPlanCard?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: leadButtonDataset.buttonAction,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      plan_id: dataset.planId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'False',
      is_plan: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getLeadButtonClickedPaidSpecCustomProperties: function to get the custom properties of lead button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getLeadButtonClickedPaidSpecCustomProperties = function(specStandardCard){
  const leadButton = specStandardCard.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || {};

  const dataset = specStandardCard.dataset;
  const destionationUrl = dataset?.url ?? specStandardCard?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: leadButtonDataset.buttonAction,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      spec_id: dataset.specId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'True',
      is_plan: 'False',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalSubmittedPaidCommunityCustomProperties: function to get the custom properties of submitted button click
* based on the community listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalSubmittedPaidCommunityCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      event_type: 'TDV Events',
      currency_value: 'USD',
      value: 30,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.priceLow,
      max_price: dataset.priceHigh,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalSubmittedPaidPlanCustomProperties: function to get the custom properties of Submitted button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalSubmittedPaidPlanCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      event_type: 'TDV Events',
      currency_value: 'USD',
      value: 30,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      plan_id: dataset.planId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'False',
      is_plan: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalSubmittedPaidSpecCustomProperties: function to get the custom properties of Submitted button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalSubmittedPaidSpecCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      event_type: 'TDV Events',
      currency_value: 'USD',
      value: 30,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      spec_id: dataset.specId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'True',
      is_plan: 'False',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getAreaClickedName: function to get the click target event based on the element clicked
* @param {HTMLElement} element target element
* @returns an string object
*/
const getAreaClickedName = function(element){
  const areaClickedPerCardElement = {
      image: 'Listings Image Clicked',
      communityName: 'Listing text link',
      planName: 'Listing text link',
      specAddress: 'Listing text link',
      favoriteButton: 'Listing favorite',
      default: 'Card Clicked',
  };
  
  const elementName = element.dataset.cardElement;
  return areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
}

/**
* getPaidCommunityCardProperties : function to return the respective custom properties
* @param {object} dataset from the standard community card element
* @returns custom properties object
*/
const getPaidCommunityCardProperties = function(data){
  const dataset = data.card.dataset;
  const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      click_target1: data.clickTarget,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.priceLow,
      max_price: dataset.priceHigh,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
};

/**
* getPaidPlanCardProperties: function to return the respective custom properties
* @param {object} dataset from the standard plan card element
* @returns custom properties object
*/
const getPaidPlanCardProperties = function(data){
  const dataset = data.card.dataset;
  const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      click_target1: data.clickTarget,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      plan_id: dataset.planId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'False',
      is_plan: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
};

/**
* getPaidSpecCardProperties: function to return the respective custom properties
* @param {object} data from the standard plan card element
* @returns custom properties object
*/
const getPaidSpecCardProperties = function(data){
  const dataset = data.card.dataset;
  const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      click_target1: data.clickTarget,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      spec_id: dataset.specId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'True',
      is_plan: 'False',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
};

/**
 * Dispatches a Analytics Event When a listing is clicked.
 * @param event - event information needed.
 */
const triggerListingCardClick = function(event) {
  store.dispatch(googleAnalytics4Event({ eventName: event.name, customProperties: event.customProperties }));
};

/**
* Dispatches a Analytics Event When the call to action is clicked.
* @param customProperties - is the custom properties of the event.
*/
const triggerLeadButtonClickedEvent = function(customProperties) {
  store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties }));
}

/**
* Dispatches a Analytics Event When the Submitt Lead Button is clicked.
* @param customProperties - is the custom properties of the event.
*/
const triggerModalSubmittedEvent = function(customProperties) {
  store.dispatch(googleAnalytics4Event({ eventName: 'generate_lead', customProperties }));
}

/**
* Dispatches a Analytics Event When the modal is closed.
* @param customProperties - is the custom properties of the event.
*/
const triggermodalCloseEvent = function(customProperties) {
  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/* An observable stream that listens for click events. It then maps each click event to the closest
ancestor element of the clicked target that has a `data-breadcrumb-item` attribute. 
The `filter(Boolean)` function is used to filter out any falsy values. */
const onBreadcrumbClick$ = bodyClick$.pipe(
  map((event) => event.target?.closest('[data-breadcrumb-item]')),
  filter(Boolean)
);

/**
 * Dispatches a Google Analytics 4 event when a breadcrumb link is clicked.
 * @param breadcrumbLink - is the link element that represents a
 * breadcrumb item in a website's navigation.
 */
const triggerBreadcrumbClickEvent = (breadcrumbLink) => {
  const breadcrumbIndex = breadcrumbItems.findIndex((item) => item === breadcrumbLink);
  const customProperties = {
    breadcrumb_position: breadcrumbIndex + 1,
    destination: breadcrumbLink.href,
    link_text: breadcrumbLink.innerText,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'breadcrumb_click', customProperties }));
};

onBreadcrumbClick$.subscribe(triggerBreadcrumbClickEvent);

/* An observable stream that listens for click events. It then maps each click event to the closest
ancestor element of the clicked target that has a `data-social-media` attribute. 
The `filter(Boolean)` function is used to filter out any falsy values. */
const onSocialLinkClick$ = bodyClick$.pipe(
  map((event) => event.target?.closest('[data-social-media]')),
  filter(Boolean)
);

/**
 * Dispatches a Google Analytics 4 event when a social link is clicked.
 * @param socialShareLink - is the link element that represents a social link.
 */
const triggerSocialShareClickEvent = (socialShareLink) => {
  const customProperties = {
    share_method: 'button',
    article_length: 'medium',
    click_target: socialShareLink.dataset.socialMedia,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'social_share', customProperties }));
};

onSocialLinkClick$.subscribe(triggerSocialShareClickEvent);

/**
 * Dispatches a Google Analytics 4 event when a search is done.
 * @param suggestion - is the suggestion element that represents the element clicked
 * in the typeahead.
 */
const triggerSuggestionGeolocationEvent = (suggestion) => {
  const customProperties = {
    search_value: suggestion.dataset.value,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_suggestion_clicked', customProperties }));
};

typeahead$.subscribe(triggerSuggestionGeolocationEvent);

/**
 * Dispatches a Analytics Event event when a navigator item is clicked.
 * @param item - is the item clicked in navigator section.
 */
const triggerNavigatorItemClick = function(item) {
  const customProperties = {
    page_type: 'Learning_Center',
    tag_text: item.dataset.text,
    destination: item.dataset.url,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'tag_click', customProperties }));
};

// Observers to navigator item section Events Logic
const onNavigatorItemClick$ = bodyClick$.pipe(
  map(getNavigatorItemElement),
  filter(Boolean)
);

onNavigatorItemClick$.subscribe(triggerNavigatorItemClick);

const triggerSearchBoxButtonClickedEvent = (inputSearchBoxValue) => {
  const customProperties = {
    search_value: inputSearchBoxValue,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_button_clicked', customProperties }));
};

const searchBoxButtonClicked$ = fromEvent(searchSubmitElement, 'click').pipe(
  filter((event) => event.target?.matches('[data-search-submit]')),
  map(() => inputSearchBox.value)
);

searchBoxButtonClicked$.subscribe(triggerSearchBoxButtonClickedEvent);

/**
 * Dispatches a Google Analytics 4 event recommendation article is clicked.
 * @param article - is the article clicked in recommendation articles section.
 */
const triggerRecomendedArticlesClick = (article) => {
  const customProperties = {
    recommendation_type: 'similar',
    click_type: 'click',
    position: +article.dataset.index + 1,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'recommendation_click', customProperties }));
};

const onRecomendedArticlesClick$ = bodyClick$.pipe(
  filter((event) => event.target.closest('[data-section-name="recommendedArticles"]')),
  map((event) => event.target.closest('[data-article-card-link]')),
  filter(Boolean)
);

onRecomendedArticlesClick$.subscribe(triggerRecomendedArticlesClick);

const triggerOnVideoInteractionEvent = ({ target }) => {
  const customProperties = {
    video_length: target.getDuration(),
    watch_time: target.getCurrentTime(),
    click_target: 'Video event',
    event_type: 'TDV Events',
    currency_value: 'USD',
    value: 4,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'video_interaction', customProperties }));
};

const attachVideosAnalyticEvents = (player) => {
  player.onPause$.subscribe(triggerOnVideoInteractionEvent);
  player.onPlay$.subscribe(triggerOnVideoInteractionEvent);
  player.onEnd$.subscribe(triggerOnVideoInteractionEvent);
};

const youtubeHandlerDownloaded = function(event){
  const videosSubjectObserver$ = event.detail.videosSubjectObserver;
  videosSubjectObserver$.subscribe(attachVideosAnalyticEvents);
}

const youtubeHandlerDownloaded$ = fromEvent(document.body, 'youtubeHandlerDownloaded');
youtubeHandlerDownloaded$.subscribe(youtubeHandlerDownloaded);

// Observers to Listing For You Section Events Logic
const cardClickEvent$ = listingForYouSectionClick$.pipe(
  filter(event => isStandardCard(event) && !isFavoriteIcon(event) && !isCallToAction(event)),
  map(event => ({
      eventName: 'card_clicked',
      clickTarget: getAreaClickedName(event.target),
      card: getStandardCardElement(event)
  }))
);

const favoriteIconClickEvent$ = listingForYouSectionClick$.pipe(
  filter(event => isFavoriteIcon(event)),
  map(event => ({
      eventName: 'saved',
      clickTarget: getAreaClickedName(event.target),
      card: getStandardCardElement(event)
  }))
);

const standardCardClickEvent$ = merge(cardClickEvent$, favoriteIconClickEvent$);

const standardSpecCardClick$ = standardCardClickEvent$.pipe(
  filter((data) => isSpecStandardCard(data.card.dataset)),
  map((data) => ({
      customProperties: getPaidSpecCardProperties(data),
      name: data.eventName
  })),
);

const standardPlanCardClick$ = standardCardClickEvent$.pipe(
  filter((data) => isPlanStandardCard(data.card.dataset)),
  map((data) => ({
      customProperties: getPaidPlanCardProperties(data),
      name: data.eventName
  })),
);

const standardCommunityCardClick$ = standardCardClickEvent$.pipe(
  filter((data) => isCommunityStandardCard(data.card.dataset)),
  map((data) => ({
      customProperties: getPaidCommunityCardProperties(data),
      name: data.eventName
  })),
);

const standardCardClickMerged$ = merge(standardSpecCardClick$, standardPlanCardClick$, standardCommunityCardClick$);
standardCardClickMerged$.subscribe(triggerListingCardClick);

// Observers to Lead Button Clicked Event Logic
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
  filter(isCallToAction),
  map(getStandardCardElement),
  filter(Boolean),
);
const callToActionSpecClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidSpecCustomProperties)
);

const callToActionPlanClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidPlanCustomProperties)
);

const callToActionCommunityClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidCommunityCustomProperties)
);

merge(
  callToActionSpecClick$,callToActionPlanClick$,callToActionCommunityClick$
).subscribe(triggerLeadButtonClickedEvent);

// Observers to ModalSubmitted Event Logic
const modalSubmittedClick$ = store$.pipe(
  filter(selectFetchingModal),
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  map((modalInformation) => ({
      listingInformation: modalInformation.listingInformation,
      url: modalInformation.url
  })),
);

const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
  filter((data) => isSpecStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidSpecCustomProperties)
);

const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
  filter((data) => isPlanStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidPlanCustomProperties)
);

const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
  filter((data) => isCommunityStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidCommunityCustomProperties)
);

merge(
  modalSubmittedSpecClick$,modalSubmittedPlanClick$,modalSubmittedCommunityClick$
).subscribe(triggerModalSubmittedEvent);

// Observers to ModalSubmitted Event Logic
const recommendedSubmitted$ = store$.pipe(
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.previousName === 'recommendedScreen'),
  filter((modalInformation) => Boolean(modalInformation.selectedRecommendedCommunities)),
  distinctUntilChanged()
);

recommendedSubmitted$.subscribe(getRecommendedSubmittedPaidCommunityCustomProperties);

const modalCloseButtonClicked$ = fromEvent(document.body, "modalClosed").pipe(
  map((event) => event?.detail?.modalInformation),
  filter(Boolean)
);

const modalCloseSpecClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isSpecStandardCard(data.listingInformation)),
  map(getModalClosePaidSpecCustomProperties)
);

const modalClosePlanClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isPlanStandardCard(data.listingInformation)),
  map(getModalClosePaidPlanCustomProperties)
);

const modalCloseCommunityClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isCommunityStandardCard(data.listingInformation)),
  map(getModalClosePaidCommunityCustomProperties)
);

merge(
  modalCloseSpecClick$,modalClosePlanClick$,modalCloseCommunityClick$
).subscribe(triggermodalCloseEvent);